import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { IUserInfo, UserTypeEnum } from "../../model/UserInfo.model";
import { prominentButtonStyle } from "../common/buttonStyles";
import { RaisedButton } from "../common/RaisedButton";
import { compareInsensitive } from "../../utils/listUtil";
import { REQUEST_STATES } from "../common/commonStates";
import { DialogComponent } from "../common/Dialog.component";
import { adminUserDetailsPair } from "../common/RequesterPairs";
import adminActions from "./adminActions";
import { IAdminState } from "./AdminReducer";
import { withVerifyAccess } from "../common/withVerifyAccess";
import { multipleApiRequesterWrapper } from "../common/MultipleApiRequesterWrapper";
import { capitalizeFirstLetter } from "../../utils/commonUtil";
import { IApplicationRootState } from "../../../applicationState";
import {navigateTo} from "../../../navigateTo";

type IMainAdminComponentPropsFromReduxStore = IAdminState;

interface IMainAdminComponentActions {
    actions: { removeUser: typeof adminActions.removeUser };
}

export type MainAdminComponentProps = IMainAdminComponentPropsFromReduxStore & IMainAdminComponentActions;

interface IMainAdminComponentState {
    dialogOpen: boolean;
    userIdToDelete: string;
    userEmailToDelete: string;
}

const DEFAULT_VISIBLE_ROWS = 10;
const MIN_SEARCH_LENGTH = 3;

export const MainAdminComponent: React.FC<MainAdminComponentProps> = (props) => {
    const navigate = useNavigate();
    const [state, setState] = useState<IMainAdminComponentState>({
        dialogOpen: false,
        userIdToDelete: "",
        userEmailToDelete: "",
    });
    const [searchQuery, setSearchQuery] = useState("");

    const sortedUsers = useMemo(() => {
        if (props.adminUsersInfoState.kind !== REQUEST_STATES.REQUEST_SUCCEEDED) {
            return [];
        }
        return [...props.adminUsersInfoState.data].sort((a, b) => compareInsensitive(a.email, b.email));
    }, [props.adminUsersInfoState]);

    const trimmedQuery = searchQuery.trim();
    const isSearching = trimmedQuery.length >= MIN_SEARCH_LENGTH;
    const isTyping = trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH;

    const filteredUsers = useMemo(() => {
        if (!isSearching) {
            return sortedUsers;
        }
        const query = trimmedQuery.toLowerCase();
        return sortedUsers.filter((user) =>
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query) ||
            user.userType.toLowerCase().includes(query) ||
            `${user.firstName} ${user.lastName}`.toLowerCase().includes(query),
        );
    }, [sortedUsers, trimmedQuery, isSearching]);

    const displayedUsers = isSearching ? filteredUsers : sortedUsers.slice(0, DEFAULT_VISIBLE_ROWS);
    const hasMore = !isSearching && sortedUsers.length > DEFAULT_VISIBLE_ROWS;

    const renderHeaders = () => (
        <tr className="admin-page__user-table-row admin-page__user-table-headers">
            <th className="admin-page__user-table-column admin-page__user-info__edit-link-header" scope="col"/>
            <th className="admin-page__user-table-column admin-page__user-info__first-name-header" scope="col">
                FIRST NAME
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__last-name-header" scope="col">
                LAST NAME
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__email-header" scope="col">
                EMAIL
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__user-type-header" scope="col">
                ROLE
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__last-login-date-header" scope="col">
                LAST LOGIN
            </th>
            <th className="admin-page__user-table-column admin-page__user-info__delete-header" scope="col"/>
        </tr>
    );

    const renderUser = (user: IUserInfo) => (
        <tr className="admin-page__user-table-row admin-page__user-info"
            key={user.arbtSecurityId}>
            <td className="admin-page__user-table-column admin-page__user-info__edit-link">
                <Link to={`/admin/user/${user.arbtSecurityId}`} id={`edit-user-link-${user.arbtSecurityId}`}>Edit</Link>
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__first-name">
                {user.firstName}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__last-name">
                {user.lastName}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__email">
                {user.email}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__user-type">
                {capitalizeFirstLetter(user.userType)}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__last-login-date">
                {user.lastLoginDate || "Never"}
            </td>
            <td className="admin-page__user-table-column admin-page__user-info__delete">
                <button
                    className="admin-page__delete-button"
                    onClick={() => deleteUser(user)}
                    aria-label={`Delete user ${user.email}`}
                >
                    <i className="fas fa-trash trash-can" aria-hidden="true"/>
                </button>
            </td>
        </tr>
    );

    const deleteUser = useCallback((user: IUserInfo) => {
        setState({
            dialogOpen: true,
            userIdToDelete: user.arbtSecurityId,
            userEmailToDelete: user.email,
        });
    }, []);

    const handleClose = useCallback(() => {
        setState((prev) => ({ ...prev, dialogOpen: false }));
    }, []);

    const handleDelete = useCallback(() => {
        props.actions.removeUser(state.userIdToDelete, state.userEmailToDelete);
        setState((prev) => ({ ...prev, dialogOpen: false }));
    }, [props.actions, state.userIdToDelete, state.userEmailToDelete]);

    const renderResultCount = () => {
        if (isTyping) {
            const remaining = MIN_SEARCH_LENGTH - trimmedQuery.length;
            return `Type ${remaining} more character${remaining > 1 ? "s" : ""} to search`;
        }
        if (isSearching) {
            return `Showing ${filteredUsers.length} of ${sortedUsers.length} users`;
        }
        if (hasMore) {
            return `Showing ${DEFAULT_VISIBLE_ROWS} of ${sortedUsers.length} users \u2014 search to find a specific user`;
        }
        return `${sortedUsers.length} users`;
    };

    const renderPage = () => (
        <div id="admin-page" className="new-common-styles" data-testid="admin-page">
            <div className="admin-page__headers">
                <h1 id="admin-page-title">User Administration</h1>
                <a className="flex-right-align link-16 clickable"
                   role="link"
                   tabIndex={0}
                   onClick={() => {navigateTo("/admin/mass-assign-users", navigate);}}
                   onKeyDown={(e) => {
                       if (e.key === "Enter" || e.key === " ") {
                           e.preventDefault();
                           navigateTo("/admin/mass-assign-users", navigate);
                       }
                   }}>
                    Mass Assign Users to Plans
                </a>
                <RaisedButton className="admin-page__add-user-button"
                              style={prominentButtonStyle}
                              primary={true}
                              onClick={() => {navigateTo("/admin/add-user", navigate);}}>
                    Add User
                </RaisedButton>
            </div>
            <div className="admin-page__spacer"/>
            <div className="admin-page__search-bar">
                <div className="admin-page__search-wrapper">
                    <i className="fas fa-magnifying-glass" aria-hidden="true"/>
                    <input
                        type="text"
                        className="admin-page__search-input"
                        placeholder="Search by name, email, or role..."
                        aria-label="Search users by name, email, or role"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                setSearchQuery("");
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        data-testid="admin-search-input"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className="admin-page__clear-btn"
                            onClick={() => setSearchQuery("")}
                            aria-label="Clear search"
                            data-testid="admin-clear-search"
                        >
                            <i className="fa-regular fa-x" aria-hidden="true"/>
                        </button>
                    )}
                </div>
                <div className="admin-page__result-count" data-testid="admin-result-count" aria-live="polite" aria-atomic="true">
                    {renderResultCount()}
                </div>
            </div>
            <div className="admin-page__search-spacer"/>
            <table className="admin-page__user-table">
                <caption className="visually-hidden">User administration table</caption>
                <thead>{renderHeaders()}</thead>
                <tbody>{displayedUsers.map((user) => renderUser(user))}</tbody>
            </table>
            {hasMore && (
                <div className="admin-page__more-indicator" data-testid="admin-more-indicator">
                    <span>
                        Showing {DEFAULT_VISIBLE_ROWS} of {sortedUsers.length} users &mdash;
                        use the search above to find a specific user
                    </span>
                </div>
            )}
            <DialogComponent
                title="Remove this user?"
                open={state.dialogOpen}
                noText="no, keep 'em"
                yesText="yes, remove 'em"
                onNo={handleClose}
                onYes={handleDelete}
                description="User will no longer have access to the website."
            />
        </div>
    );

    return props.adminUsersInfoState.kind === REQUEST_STATES.REQUEST_SUCCEEDED
        ? renderPage()
        : null;
};

export const mapDispatchToProps = (dispatch: Dispatch): IMainAdminComponentActions => ({
    actions: bindActionCreators({
        removeUser: adminActions.removeUser,
    }, dispatch),
});

export const mapStateToProps = (state: IApplicationRootState): IMainAdminComponentPropsFromReduxStore => ({
    ...state.adminState!,
});

const connectedComponent = connect<IMainAdminComponentPropsFromReduxStore, IMainAdminComponentActions>(
    mapStateToProps,
    mapDispatchToProps
)(MainAdminComponent);

const withRequester = multipleApiRequesterWrapper(
    connectedComponent,
    [
        adminUserDetailsPair,
    ],
);

export default withVerifyAccess(withRequester, UserTypeEnum.ADMIN);